import { useState } from 'react';
import { CourseSessionPublic } from '@/types/course';
import {
  CheckCircleIcon,
  ChevronDownIcon,
  LightBulbIcon,
} from '@heroicons/react/24/outline';

interface CourseSessionListProps {
  sessions: CourseSessionPublic[];
  className?: string;
}

const HTML_PROSE =
  'text-sm text-m2-on-surface leading-relaxed whitespace-pre-wrap [&_img]:max-w-full [&_img]:h-auto [&_ul]:my-0 [&_ul]:list-disc [&_ul]:space-y-2.5 [&_ul]:pl-5 [&_ol]:my-0 [&_ol]:list-decimal [&_ol]:space-y-2.5 [&_ol]:pl-5 [&_li]:pl-1 [&_li]:marker:font-semibold [&_li]:marker:text-m2-primary [&_p]:mb-2 last:[&_p]:mb-0';

/** Strip leading "Buổi N:" / "Buổi N -" so titles stand alone. */
function displayTitle(title: string): string {
  const cleaned = title
    .replace(/^\s*Buổi\s*\d+\s*[:.\-–—]?\s*/i, '')
    .trim();
  return cleaned || title;
}

function padSession(orderIndex: number): string {
  return String(orderIndex).padStart(2, '0');
}

const CourseSessionList = ({ sessions, className = '' }: CourseSessionListProps) => {
  const [openId, setOpenId] = useState<number | null>(sessions[0]?.id ?? null);

  if (!sessions.length) {
    return <p className="text-m2-on-surface-variant">Nội dung sẽ được cập nhật sớm.</p>;
  }

  return (
    <div
      className={`overflow-hidden rounded-2xl border border-m2-outline-variant/80 bg-m2-surface-container shadow-sm divide-y divide-m2-outline-variant/70 ${className}`}
    >
      {sessions.map((session) => {
        const isOpen = openId === session.id;
        const hasContent = Boolean(session.content?.trim());
        const hasOutput = Boolean(session.output?.trim());
        const hasBody = hasContent || hasOutput;
        const title = displayTitle(session.title);

        return (
          <div
            key={session.id}
            className={`transition-colors ${isOpen ? 'bg-m2-surface-low/40' : 'bg-m2-surface-container'}`}
          >
            <button
              type="button"
              className="group w-full flex items-center gap-3 px-4 py-4 text-left sm:px-5 sm:gap-4 hover:bg-m2-surface-low/60 transition-colors"
              onClick={() => setOpenId(isOpen ? null : session.id)}
              aria-expanded={isOpen}
            >
              <span
                className={`inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-xs font-bold tracking-wide transition-colors ${
                  isOpen
                    ? 'bg-m2-primary text-white shadow-sm'
                    : 'bg-m2-primary/10 text-m2-primary group-hover:bg-m2-primary/15'
                }`}
                aria-hidden
              >
                {padSession(session.orderIndex)}
              </span>

              <p
                className={`min-w-0 flex-1 text-base font-semibold leading-snug text-m2-on-surface sm:text-[17px] ${
                  isOpen ? '' : 'group-hover:text-m2-primary transition-colors'
                }`}
              >
                {title}
              </p>

              <ChevronDownIcon
                className={`h-5 w-5 shrink-0 text-m2-outline transition-transform duration-200 ${
                  isOpen ? 'rotate-180 text-m2-primary' : 'group-hover:text-m2-on-surface-variant'
                }`}
              />
            </button>

            {isOpen && (
              hasBody ? (
                <div className="space-y-4 px-4 pb-5 pt-0 sm:px-5 sm:pl-[4.25rem]">
                  {hasContent && (
                    <div className="rounded-xl border border-m2-outline-variant/60 bg-white/70 p-4 sm:p-5">
                      <div className="mb-3 flex items-center gap-2">
                        <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-m2-primary/10 text-m2-primary">
                          <LightBulbIcon className="h-4 w-4" />
                        </span>
                        <p className="text-sm font-bold text-m2-on-surface">Bạn sẽ học</p>
                      </div>
                      <div
                        className={HTML_PROSE}
                        dangerouslySetInnerHTML={{ __html: session.content! }}
                      />
                    </div>
                  )}

                  {hasOutput && (
                    <div className="rounded-xl border border-m2-primary/20 bg-gradient-to-br from-m2-primary/[0.08] to-m2-primary/[0.02] p-4 sm:p-5">
                      <div className="mb-3 flex items-center gap-2">
                        <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-m2-primary/15 text-m2-primary">
                          <CheckCircleIcon className="h-4 w-4" />
                        </span>
                        <p className="text-sm font-bold text-m2-on-surface">
                          Kết quả sau buổi học
                        </p>
                      </div>
                      <div
                        className={HTML_PROSE}
                        dangerouslySetInnerHTML={{ __html: session.output! }}
                      />
                    </div>
                  )}
                </div>
              ) : (
                <p className="px-4 pb-5 text-sm text-m2-on-surface-variant sm:pl-[4.25rem]">
                  Chi tiết buổi học đang cập nhật.
                </p>
              )
            )}
          </div>
        );
      })}
    </div>
  );
};

export default CourseSessionList;
